import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
} from 'react';
import type { SaveAnswersResponse } from '../types/api';
import { hotelQuestionnaireReducer, initialState } from './reducers';
import { hotelQuestionnaireActions } from './actions';
import {
  fetchStepsAsync,
  refreshStepsAsync,
  fetchQuestionnaireAsync,
  saveAnswersAsync,
  goToStepAsync,
  fetchSourcesAsync,
  submitQuestionnaireAsync,
  fetchReadonlyQuestionnaireAsync,
  fetchSubmissionsAsync,
} from './actions/hotelQuestionnaireAsyncActions';
import { HotelQuestionnaireContextType } from './types';

// Create context
const HotelQuestionnaireContext = createContext<
  HotelQuestionnaireContextType | undefined
>(undefined);

// Provider component
interface HotelQuestionnaireProviderProps {
  children: React.ReactNode;
  hotelId?: string;
}

export const HotelQuestionnaireProvider: React.FC<
  HotelQuestionnaireProviderProps
> = ({ children, hotelId }) => {
  const [state, dispatch] = useReducer(hotelQuestionnaireReducer, initialState);
  // Removed console.log to prevent excessive logging on every render
  // console.log('HotelQuestionnaireProvider State::', {
  //   answers: state.answers,
  //   draftAnswers: state.draftAnswers,
  //   draftAnswersCount: Object.keys(state.draftAnswers).length,
  // });

  // Set hotel ID when provider mounts or hotelId prop changes
  useEffect(() => {
    if (hotelId && hotelId !== state.hotelId) {
      dispatch(hotelQuestionnaireActions.setHotelId(hotelId));
    }
  }, [hotelId, state.hotelId]);

  // Actions
  const setHotelId = useCallback((hotelId: string) => {
    dispatch(hotelQuestionnaireActions.setHotelId(hotelId));
  }, []);

  const fetchSteps = useCallback(async (isFasttackOverride?: boolean) => {
    if (!state.hotelId) return;
    const isFasttrack = isFasttackOverride !== undefined ? isFasttackOverride : (state.questionnaireType === 'FASTTRACK');
    await fetchStepsAsync(dispatch)(state.hotelId, isFasttrack);
  }, [state.hotelId, state.questionnaireType]);

  const refreshSteps = useCallback(async (currentStepKey?: string, persistStep: boolean = false, isFasttrackOverride?: boolean) => {
    if (!state.hotelId) return;
    const isFasttrack = isFasttrackOverride !== undefined ? isFasttrackOverride : (state.questionnaireType === 'FASTTRACK');
    await refreshStepsAsync(dispatch)(state.hotelId, currentStepKey, persistStep, isFasttrack);
  }, [state.hotelId, state.questionnaireType]);

  const fetchQuestionnaire = useCallback(
    async (stepKey: string, submissionId?: string | null) => {
      if (!state.hotelId || !stepKey) return;
      const currentSubmissionId = state.selectedSubmissionId;
      console.log('Current selectedSubmissionId in state:', currentSubmissionId);
      await fetchQuestionnaireAsync(dispatch)(state.hotelId, stepKey, currentSubmissionId || undefined, state.questionnaireType === 'FASTTRACK');
    },
    [state.hotelId, state.selectedSubmissionId, state.questionnaireType]
  );

  const updateAnswer = useCallback(
    (questionId: string, value: any, note?: string) => {
      dispatch(hotelQuestionnaireActions.updateAnswer(questionId, value, note));
      
      // Check if the question that was answered has a dependency_list
      // If yes, trigger exclusion state update for dependent questions
      if (state.questionnaireData) {
        let hasDependencyList = false;

        if ('hasGroups' in state.questionnaireData && state.questionnaireData.hasGroups) {
          // Grouped questionnaire: check all groups for the question
          const groupedData = state.questionnaireData as any;
          const allQuestions = groupedData.groups?.flatMap((g: any) => g.metadata.fields || []) || [];
          const question = allQuestions.find((q: any) => q.id === questionId);
          hasDependencyList = question?.dependency_list && question.dependency_list.length > 0;
        } else {
          // Standard questionnaire: check metadata.fields
          const standardData = state.questionnaireData as any;
          const { findQuestionById } = require('../utils/dependencyUtils');
          const question = findQuestionById(standardData.metadata.fields, questionId);
          hasDependencyList = question?.dependency_list && question.dependency_list.length > 0;
        }

        if (hasDependencyList) {
          dispatch(hotelQuestionnaireActions.updateQuestionExclusionStates(questionId));
        }
      }
    },
    [state.questionnaireData]
  );

  const updateDraftAnswer = useCallback(
    (questionId: string, value: any, note?: string) => {
      dispatch(
        hotelQuestionnaireActions.updateDraftAnswer(questionId, value, note)
      );
      
      // Check if the question that was answered has a dependency_list
      // If yes, trigger exclusion state update for dependent questions
      if (state.questionnaireData) {
        let hasDependencyList = false;

        if ('hasGroups' in state.questionnaireData && state.questionnaireData.hasGroups) {
          // Grouped questionnaire: check all groups for the question
          const groupedData = state.questionnaireData as any;
          const allQuestions = groupedData.groups?.flatMap((g: any) => g.metadata.fields || []) || [];
          const question = allQuestions.find((q: any) => q.id === questionId);
          hasDependencyList = question?.dependency_list && question.dependency_list.length > 0;
        } else {
          // Standard questionnaire: check metadata.fields
          const standardData = state.questionnaireData as any;
          const { findQuestionById } = require('../utils/dependencyUtils');
          const question = findQuestionById(standardData.metadata.fields, questionId);
          hasDependencyList = question?.dependency_list && question.dependency_list.length > 0;
        }
        // console.log("The question hasDependencyList??", hasDependencyList);
        if (hasDependencyList) {
          dispatch(hotelQuestionnaireActions.updateQuestionExclusionStates(questionId));
        }
      }
    },
    [state.questionnaireData]
  );

  const applyDraftAnswers = useCallback(() => {
    dispatch(hotelQuestionnaireActions.applyDraftAnswers());
  }, []);

  const clearDraftAnswers = useCallback(() => {
    dispatch(hotelQuestionnaireActions.clearDraftAnswers());
  }, []);

  const hasDraftChanges = useCallback(() => {
    return Object.keys(state.draftAnswers).length > 0;
  }, [state.draftAnswers]);

  const updateQuestionUploadedFilenames = useCallback(
    (questionId: string, uploadedFilenames: string[]) => {
      dispatch(
        hotelQuestionnaireActions.updateQuestionUploadedFilenames(
          questionId,
          uploadedFilenames
        )
      );
    },
    []
  );

  const saveAnswers = useCallback(
    async (isDraft = false): Promise<SaveAnswersResponse | null> => {
      return await saveAnswersAsync(
        dispatch,
        state,
        refreshSteps,
        fetchQuestionnaire,
        updateQuestionUploadedFilenames
      )(isDraft);
    },
    [state, refreshSteps, fetchQuestionnaire, updateQuestionUploadedFilenames]
  );

  const goToStep = useCallback(
    async (stepKey: string) => {
      if (!state.hotelId) return;
      // console.log('Go to step ::', stepKey);
      // console.log('From Step ::', state.stepsData?.currentStepKey);

      // Clear targetSourceGroup when navigating away from sources
      if (
        state.stepsData?.currentStepKey === 'sources' &&
        stepKey !== 'sources'
      ) {
        dispatch(hotelQuestionnaireActions.setTargetSourceGroup(null));
      }

      // Set sources loading immediately if navigating to sources
      if (stepKey === 'sources') {
        dispatch(hotelQuestionnaireActions.setSourcesLoading(true));
      }

      const currentSubmissionId = state.selectedSubmissionId;
      console.log('Current selectedSubmissionId in state before goToStep:', currentSubmissionId);

      await goToStepAsync(dispatch)(state.hotelId, stepKey, currentSubmissionId || undefined, state.questionnaireType === 'FASTTRACK');
    },
    [state.hotelId, state.stepsData?.currentStepKey, state.selectedSubmissionId, state.questionnaireType]
  );
  const fetchSources = useCallback(async () => {
    if (!state.hotelId) return;
    const currentSubmissionId = state.selectedSubmissionId;
    await fetchSourcesAsync(dispatch)(state.hotelId, currentSubmissionId || undefined, state.questionnaireType === 'FASTTRACK');
  }, [state.hotelId, state.selectedSubmissionId, state.questionnaireType]);

  const fetchReadonlyQuestionnaire = useCallback(async () => {
    if (!state.hotelId) return;
    await fetchReadonlyQuestionnaireAsync(dispatch)(state.hotelId);
  }, [state.hotelId]);

  const resetState = useCallback(() => {
    dispatch(hotelQuestionnaireActions.resetState());
  }, []);
  const submitQuestionnaire = useCallback(async () => {
    if (!state.hotelId) return;
    await submitQuestionnaireAsync(dispatch, goToStep)(state.hotelId, state.questionnaireType === 'FASTTRACK');
  }, [state.hotelId, goToStep, state.questionnaireType]);

  const updateQuestionAttachments = useCallback(
    (questionId: string, attachments: any[]) => {
      dispatch(
        hotelQuestionnaireActions.updateQuestionAttachments(
          questionId,
          attachments
        )
      );
    },
    []
  );

  const updateQuestionExclusionStates = useCallback((questionId?: string) => {
    // This function is now used internally by updateAnswer/updateDraftAnswer
    // If called directly without questionId, do nothing (old behavior compatibility)
    if (questionId) {
      dispatch(hotelQuestionnaireActions.updateQuestionExclusionStates(questionId));
    }
  }, []);

  const cleanupExcludedQuestionAnswers = useCallback(() => {
    dispatch(hotelQuestionnaireActions.cleanupExcludedQuestionAnswers());
  }, []);

  const setReviews = useCallback(
    (reviews: Record<string, any>) => {
      dispatch(hotelQuestionnaireActions.setReviews(reviews));
    },
    []
  );

  const updateReview = useCallback(
    (questionId: string, approved: boolean | null, reasonId?: string | null) => {
      dispatch(
        hotelQuestionnaireActions.updateReview(questionId, approved, reasonId)
      );
    },
    []
  );

  const updateDraftReview = useCallback(
    (questionId: string, approved: boolean | null, reasonId?: string | null) => {
      dispatch(
        hotelQuestionnaireActions.updateDraftReview(questionId, approved, reasonId)
      );
    },
    []
  );

  const applyDraftReviews = useCallback(() => {
    dispatch(hotelQuestionnaireActions.applyDraftReviews());
  }, []);

  const clearDraftReviews = useCallback(() => {
    dispatch(hotelQuestionnaireActions.clearDraftReviews());
  }, []);

  const hasDraftReviews = useCallback(() => {
    return Object.keys(state.draftReviews).length > 0;
  }, [state.draftReviews]);

  const saveReviews = useCallback(async () => {
    if (!state.hotelId) return { success: false };

    try {
      // Combine main reviews with draft reviews (draft takes precedence)
      const combinedReviews = { ...state.reviews, ...state.draftReviews };
      // Transform to backend format
      const reviewsPayload: Record<string, any> = {};
      Object.keys(combinedReviews).forEach((questionId) => {
        const review = combinedReviews[questionId];
        reviewsPayload[questionId] = {
          approved: review.approved,
          reason_id: review.reasonId || null,
        };
      });

      // Import API utilities
      const { apiUtils } = await import('../hooks/useApi');
      const { API_ENDPOINTS } = await import('../services/apiEndpoints');

      // Check for missing attachments in sources if sourcesData is available
      let hasMissingAttachments = false;
      if (state.sourcesData) {
        const { checkForMissingAttachments } = await import('../utils/attachmentValidationUtils');
        
        // Create a temporary answer structure from sources data
        const sourceAnswers: Record<string, any> = {};
        state.sourcesData.groups.forEach((group: any) => {
          group.metadata.fields.forEach((field: any) => {
            if (field.selected_option_id) {
              sourceAnswers[field.id] = {
                questionId: field.id,
                value: {
                  selectedOptionId: field.selected_option_id,
                  typedValue: null,
                },
              };
            }
          });
        });
        
        hasMissingAttachments = checkForMissingAttachments(state.sourcesData as any, sourceAnswers);
      }

      // Save to backend
      await apiUtils.post(
        API_ENDPOINTS.hotels.saveSources(state.hotelId),
        { reviews: reviewsPayload ,
          questionnaire_type: state.questionnaireType,
        }
      );

      // If save is successful, apply draft reviews to main state and clear draft
      dispatch(hotelQuestionnaireActions.applyDraftReviews());

      // Refresh sources to get updated data
      await fetchSourcesAsync(dispatch)(state.hotelId, state.selectedSubmissionId || undefined, state.questionnaireType === 'FASTTRACK');
      
      return { success: true, hasMissingAttachments };
    } catch (err) {
      console.error('❌ Failed to save reviews:', err);
      throw err;
    }
  }, [state.hotelId, state.reviews, state.draftReviews, state.sourcesData, state.selectedSubmissionId, state.questionnaireType]);

  const setTargetSourceGroup = useCallback((groupKey: string | null) => {
    // console.log('Setting targetSourceGroup to:', groupKey);
    dispatch(hotelQuestionnaireActions.setTargetSourceGroup(groupKey));
  }, []);

  const goToSourcesWithGroup = useCallback(
    async (targetGroup?: string) => {
      // console.log('goToSourcesWithGroup called with:', targetGroup);
      if (targetGroup) {
        setTargetSourceGroup(targetGroup);
      }
      // Set sources loading immediately before navigation
      dispatch(hotelQuestionnaireActions.setSourcesLoading(true));
      await goToStep('sources');
    },
    [goToStep, setTargetSourceGroup]
  );

  const fetchSubmissions = useCallback(async (isFasttackOverride?: boolean) => {
    if (!state.hotelId) return;
    const isFasttrack = isFasttackOverride !== undefined ? isFasttackOverride : (state.questionnaireType === 'FASTTRACK');
    await fetchSubmissionsAsync(dispatch)(state.hotelId, isFasttrack);
  }, [state.hotelId, state.questionnaireType]);

  const setSelectedSubmissionId = useCallback((submissionId: string | null) => {
    dispatch(hotelQuestionnaireActions.setSelectedSubmissionId(submissionId));
  }, []);

  const setQuestionnaireType = useCallback((type: 'FASTTRACK' | 'FULL' | null) => {
    dispatch(hotelQuestionnaireActions.setQuestionnaireType(type));
  }, []);

  const setSwitchingType = useCallback((value: boolean) => {
    dispatch(hotelQuestionnaireActions.setSwitchingType(value));
  }, []);

  // Auto-fetch steps when hotelId changes
  useEffect(() => {
    if (state.hotelId) {
      fetchStepsAsync(dispatch)(state.hotelId, state.questionnaireType === 'FASTTRACK');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.hotelId]);

  const contextValue: HotelQuestionnaireContextType = {
    state,
    actions: {
      setHotelId,
      fetchSteps,
      refreshSteps,
      fetchQuestionnaire,
      submitQuestionnaire,
      updateAnswer,
      updateDraftAnswer,
      applyDraftAnswers,
      clearDraftAnswers,
      hasDraftChanges,
      saveAnswers,
      goToStep,
      goToSourcesWithGroup,
      fetchSources,
      fetchReadonlyQuestionnaire,
      setTargetSourceGroup,
      resetState,
      updateQuestionAttachments,
      updateQuestionUploadedFilenames,
      updateQuestionExclusionStates,
      cleanupExcludedQuestionAnswers,
      setReviews,
      updateReview,
      updateDraftReview,
      applyDraftReviews,
      clearDraftReviews,
      hasDraftReviews,
      saveReviews,
      fetchSubmissions,
      setSelectedSubmissionId,
      setQuestionnaireType,
      setSwitchingType,
    },
  };

  return (
    <HotelQuestionnaireContext.Provider value={contextValue}>
      {children}
    </HotelQuestionnaireContext.Provider>
  );
};

// Hook to use the context
export const useHotelQuestionnaireContext =
  (): HotelQuestionnaireContextType => {
    const context = useContext(HotelQuestionnaireContext);
    if (context === undefined) {
      throw new Error(
        'useHotelQuestionnaireContext must be used within a HotelQuestionnaireProvider'
      );
    }
    return context;
  };
